/**
 * Shapes shared between the browser and the Lambda handlers.
 * Changing anything here is a wire-format change — update both sides.
 */

/** Moderation state. Only `approved` submissions are ever served publicly. */
export type SubmissionStatus = 'pending' | 'approved' | 'rejected';

/** A submission as returned to the public wall. Deliberately minimal. */
export interface PublicSubmission {
  id: string;
  /** Display name the submitter chose. Never an email or anything contactable. */
  name: string;
  /** Optional free-text note, length-capped server-side. */
  note?: string;
  /** Public CloudFront/S3 URL of the approved photo, if one was uploaded. */
  photoUrl?: string;
  /**
   * Public URL of the ~600px WebP thumbnail generated at approval time. The wall
   * renders this instead of the full-size photoUrl to cut egress, and clicks
   * through to photoUrl. Absent on records approved before thumbnails existed
   * and on text-only submissions, so the client must fall back to photoUrl.
   */
  thumbnailUrl?: string;
  /** ISO 8601, set server-side at submit time. */
  createdAt: string;
}

export interface ListSubmissionsResponse {
  submissions: PublicSubmission[];
  /** Opaque cursor for the next page; absent when there are no more. */
  nextCursor?: string;
}

/**
 * A submission exactly as stored — DynamoDB keys and all. This is what the
 * admin moderation routes hand back: the moderator sees the raw record,
 * including the contact email and the not-yet-public photoKey, because
 * reviewing a submission means looking at material the wall has not accepted.
 *
 * It lives here rather than only in the Lambda's `model.ts` because both sides
 * must name the same shape and the browser TypeScript project deliberately
 * excludes `src/functions`. `model.ts` re-exports it.
 */
export interface SubmissionItem {
  PK: string;
  SK: string;
  GSI1PK: string;
  GSI1SK: string;
  id: string;
  name: string;
  note?: string;
  /** Contact email for campaign updates. Moderator-visible, never public. */
  email?: string;
  /**
   * DC ward the submitter is from, as a bare number string '1'..'8' (see
   * DC_WARDS). Optional, internal tracking only — moderator-visible, NEVER
   * public (kept out of PublicSubmission, same as email).
   */
  ward?: string;
  /**
   * US state the submitter is from, as a USPS 2-letter code (see US_STATES /
   * US_STATE_CODES), e.g. 'DC', 'MD', 'VA'. Optional, internal tracking only —
   * moderator-visible, NEVER public. A ward is only meaningful when the state is
   * 'DC'. Older records predate this field, so it may be absent on read.
   */
  state?: string;
  /**
   * Country the submitter is from, as an ISO 3166-1 alpha-2 code (see COUNTRIES
   * / COUNTRY_CODES), e.g. 'US', 'CA', 'GB'. Optional, internal tracking only --
   * moderator-visible, NEVER public. State and ward are only meaningful when the
   * country is 'US'. Older records predate this field, so absent means 'US' on read.
   */
  country?: string;
  photoKey?: string;
  /**
   * Whether the submitter consented to being shown on the public wall. The form
   * defaults it to true (opt-out): unchecking the consent box sends false, which
   * keeps the submission recorded (the campaign still gets the action + optional
   * email) but must NEVER reach the wall, even once a moderator approves it (see
   * listSubmissions' `item.public !== false` filter). Moderator-visible. Absent
   * means true: legacy records predate the flag and were all potentially public.
   */
  public?: boolean;
  /**
   * Source attribution slug, captured from a `?ref=<slug>` reference link the
   * submitter arrived through (see normalizeRef / MAX_REF_LENGTH). It is how the
   * campaign tells which partner org's link produced a signup. Internal tracking
   * only -- moderator-visible, NEVER public, same posture as email/ward/state.
   * Absent on direct traffic and on every record written before this existed.
   */
  ref?: string;
  /**
   * S3 key of the ~600px WebP thumbnail, set at approval time once the uploaded
   * bytes are verified to be a real image (see the approval pipeline in the api
   * Lambda). Absent while pending, on rejected items, on text-only submissions,
   * and on photos approved before thumbnails existed. `toPublicSubmission`
   * turns it into thumbnailUrl.
   */
  thumbKey?: string;
  status: SubmissionStatus;
  createdAt: string;
  /** Set when a moderator acts on it. Absent while pending. */
  moderatedAt?: string;
  /**
   * ISO 8601 time an admin last changed the item's wall visibility (currently
   * only the one-way "make private" action, which sets `public: false`). Absent
   * until that action is taken. Audit breadcrumb only -- never public.
   */
  visibilityUpdatedAt?: string;
}

/** Response shape of `GET /admin/submissions`. Raw items, not PublicSubmissions. */
export interface AdminListResponse {
  submissions: SubmissionItem[];
  nextCursor?: string;
}

/** Response shape of `POST /admin/submissions/{id}`. */
export interface ModerateResponse {
  id: string;
  status: SubmissionStatus;
}

/**
 * Response shape of `POST /admin/submissions/{id}/hide`. The action is ONE-WAY:
 * an approved, public submission is taken off the wall by setting `public` to
 * false. There is no re-publish route, so the flag is always the literal
 * `false` here, never a variable boolean.
 */
export interface HideResponse {
  id: string;
  public: false;
}

export interface CreateSubmissionRequest {
  name: string;
  note?: string;
  /**
   * Optional contact email, collected only for campaign updates. Stored on the
   * submission item and visible to the moderator — NEVER returned by any public
   * route (see PublicSubmission).
   */
  email?: string;
  /**
   * Optional DC ward '1'..'8' (see DC_WARDS). Internal tracking only — stored
   * on the submission and visible to the moderator, NEVER returned publicly.
   */
  ward?: string;
  /**
   * Optional US state as a USPS 2-letter code (see US_STATES / US_STATE_CODES).
   * Internal tracking only, NEVER returned publicly. Absent/empty is treated as
   * 'DC' server-side, so old clients keep working and a DC ward stays valid.
   */
  state?: string;
  /**
   * Optional country as an ISO 3166-1 alpha-2 code (see COUNTRIES / COUNTRY_CODES).
   * Internal tracking only, NEVER returned publicly. Absent/empty is treated as
   * 'US' server-side, so old clients keep working and a US state/ward stays valid.
   */
  country?: string;
  /**
   * The S3 object key returned by the upload-url endpoint, if the user attached
   * a photo. The server re-derives the URL from this — it never trusts a
   * client-supplied URL.
   */
  photoKey?: string;
  /**
   * Consent to public wall display. The form sends true unless the submitter
   * unchecks the consent box, in which case it sends false and the submission
   * is recorded but never shown publicly. Absent defaults to true server-side,
   * so old clients that never send it keep working.
   */
  public?: boolean;
  /**
   * Optional source attribution slug the browser captured from a `?ref=<slug>`
   * reference link (see normalizeRef). Internal tracking only, NEVER returned
   * publicly. The server normalizes it and SILENTLY DROPS anything malformed:
   * a signup must never fail because of a broken tracking tag.
   */
  ref?: string;
}

export interface CreateSubmissionResponse {
  id: string;
  status: SubmissionStatus;
}

export interface UploadUrlRequest {
  /** MIME type of the file to upload. Server enforces the image/* allowlist. */
  contentType: string;
  /** Size in bytes, so the server can reject oversized files before signing. */
  contentLength: number;
}

export interface UploadUrlResponse {
  /** Short-lived presigned PUT URL, scoped to exactly one key. */
  uploadUrl: string;
  /** Pass this back to the create-submission endpoint as `photoKey`. */
  key: string;
}

export interface ApiError {
  error: string;
}

/** Upload limits, shared so the client can pre-validate with the same numbers. */
export const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
export const ALLOWED_PHOTO_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
] as const;

/**
 * Reference-link slugs (`https://deletedoordash.com/?ref=<slug>`), the tag a
 * partner org's link carries so page views and signups can be attributed to it.
 *
 * Deliberately narrow: 1..40 characters of lowercase letters, digits, and
 * hyphens. Narrow because this value is (a) written into a bucket key that the
 * admin overview renders as a chart label, and (b) tallied from log lines that
 * any stranger on the internet can spray with junk. Anything outside the set is
 * not a slug we minted, so it is not counted anywhere.
 */
export const MAX_REF_LENGTH = 40;
/** Built from MAX_REF_LENGTH so the cap and the pattern cannot disagree. */
export const REF_PATTERN = new RegExp(`^[a-z0-9-]{1,${MAX_REF_LENGTH}}$`);

/**
 * Normalize an untrusted ref (query string, localStorage, POST body, log line)
 * to the canonical slug, or null when it is not one.
 *
 * Case-folds first so `?ref=Free-DC` and `?ref=free-dc` are the same org rather
 * than two bars on the chart. Length is checked by the pattern itself. ONE
 * implementation, shared by the browser, the API Lambda, and the traffic-agg
 * Lambda, so the three cannot drift on what counts as the same reference.
 */
export function normalizeRef(raw: string): string | null {
  if (typeof raw !== 'string') return null;
  const slug = raw.trim().toLowerCase();
  return REF_PATTERN.test(slug) ? slug : null;
}

export const MAX_NAME_LENGTH = 60;
export const MAX_NOTE_LENGTH = 280;
/** RFC 5321's practical ceiling for an address. */
export const MAX_EMAIL_LENGTH = 254;

/**
 * The eight wards of the District of Columbia, as bare number strings. This is
 * the allowed set the server validates ward against and the browser builds the
 * dropdown from — one source of truth so the two cannot drift.
 */
export const DC_WARDS = ['1', '2', '3', '4', '5', '6', '7', '8'] as const;

/**
 * The 50 states plus the District of Columbia, as USPS 2-letter codes paired
 * with display names. DC is listed first so it is the natural top option and
 * default in the form; the rest follow alphabetically by name. This is the one
 * source of truth the server validates `state` against and the browser builds
 * its dropdown from, so the two cannot drift. No territories: 50 states + DC.
 */
export const US_STATES = [
  { code: 'DC', name: 'District of Columbia' },
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
] as const;

/**
 * The set of valid `state` codes, derived from US_STATES so the two cannot
 * drift — mirrors DC_WARDS as the server-side membership check.
 */
export const US_STATE_CODES: readonly string[] = US_STATES.map((s) => s.code);

/** The default state when none is supplied. DC, the campaign's home turf. */
export const DEFAULT_STATE = 'DC';

/**
 * Countries as ISO 3166-1 alpha-2 codes paired with display names. The United
 * States is listed first so it is the natural top option and the default in the
 * form; the rest follow alphabetically by name. This is the one source of truth
 * the server validates `country` against and the browser builds its dropdown
 * from, so the two cannot drift -- exactly as US_STATES does for `state`. A
 * ward and a US state are only meaningful when the country is 'US'.
 */
export const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'AF', name: 'Afghanistan' },
  { code: 'AL', name: 'Albania' },
  { code: 'DZ', name: 'Algeria' },
  { code: 'AD', name: 'Andorra' },
  { code: 'AO', name: 'Angola' },
  { code: 'AG', name: 'Antigua and Barbuda' },
  { code: 'AR', name: 'Argentina' },
  { code: 'AM', name: 'Armenia' },
  { code: 'AU', name: 'Australia' },
  { code: 'AT', name: 'Austria' },
  { code: 'AZ', name: 'Azerbaijan' },
  { code: 'BS', name: 'Bahamas' },
  { code: 'BH', name: 'Bahrain' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'BB', name: 'Barbados' },
  { code: 'BY', name: 'Belarus' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BZ', name: 'Belize' },
  { code: 'BJ', name: 'Benin' },
  { code: 'BT', name: 'Bhutan' },
  { code: 'BO', name: 'Bolivia' },
  { code: 'BA', name: 'Bosnia and Herzegovina' },
  { code: 'BW', name: 'Botswana' },
  { code: 'BR', name: 'Brazil' },
  { code: 'BN', name: 'Brunei' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'BF', name: 'Burkina Faso' },
  { code: 'BI', name: 'Burundi' },
  { code: 'CV', name: 'Cabo Verde' },
  { code: 'KH', name: 'Cambodia' },
  { code: 'CM', name: 'Cameroon' },
  { code: 'CA', name: 'Canada' },
  { code: 'CF', name: 'Central African Republic' },
  { code: 'TD', name: 'Chad' },
  { code: 'CL', name: 'Chile' },
  { code: 'CN', name: 'China' },
  { code: 'CO', name: 'Colombia' },
  { code: 'KM', name: 'Comoros' },
  { code: 'CG', name: 'Congo (Republic)' },
  { code: 'CD', name: 'Congo (DRC)' },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'CI', name: "Cote d'Ivoire" },
  { code: 'HR', name: 'Croatia' },
  { code: 'CU', name: 'Cuba' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'CZ', name: 'Czechia' },
  { code: 'DK', name: 'Denmark' },
  { code: 'DJ', name: 'Djibouti' },
  { code: 'DM', name: 'Dominica' },
  { code: 'DO', name: 'Dominican Republic' },
  { code: 'EC', name: 'Ecuador' },
  { code: 'EG', name: 'Egypt' },
  { code: 'SV', name: 'El Salvador' },
  { code: 'GQ', name: 'Equatorial Guinea' },
  { code: 'ER', name: 'Eritrea' },
  { code: 'EE', name: 'Estonia' },
  { code: 'SZ', name: 'Eswatini' },
  { code: 'ET', name: 'Ethiopia' },
  { code: 'FJ', name: 'Fiji' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'GA', name: 'Gabon' },
  { code: 'GM', name: 'Gambia' },
  { code: 'GE', name: 'Georgia' },
  { code: 'DE', name: 'Germany' },
  { code: 'GH', name: 'Ghana' },
  { code: 'GR', name: 'Greece' },
  { code: 'GD', name: 'Grenada' },
  { code: 'GT', name: 'Guatemala' },
  { code: 'GN', name: 'Guinea' },
  { code: 'GW', name: 'Guinea-Bissau' },
  { code: 'GY', name: 'Guyana' },
  { code: 'HT', name: 'Haiti' },
  { code: 'HN', name: 'Honduras' },
  { code: 'HU', name: 'Hungary' },
  { code: 'IS', name: 'Iceland' },
  { code: 'IN', name: 'India' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'IR', name: 'Iran' },
  { code: 'IQ', name: 'Iraq' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IL', name: 'Israel' },
  { code: 'IT', name: 'Italy' },
  { code: 'JM', name: 'Jamaica' },
  { code: 'JP', name: 'Japan' },
  { code: 'JO', name: 'Jordan' },
  { code: 'KZ', name: 'Kazakhstan' },
  { code: 'KE', name: 'Kenya' },
  { code: 'KI', name: 'Kiribati' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'KG', name: 'Kyrgyzstan' },
  { code: 'LA', name: 'Laos' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LB', name: 'Lebanon' },
  { code: 'LS', name: 'Lesotho' },
  { code: 'LR', name: 'Liberia' },
  { code: 'LY', name: 'Libya' },
  { code: 'LI', name: 'Liechtenstein' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MG', name: 'Madagascar' },
  { code: 'MW', name: 'Malawi' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'MV', name: 'Maldives' },
  { code: 'ML', name: 'Mali' },
  { code: 'MT', name: 'Malta' },
  { code: 'MH', name: 'Marshall Islands' },
  { code: 'MR', name: 'Mauritania' },
  { code: 'MU', name: 'Mauritius' },
  { code: 'MX', name: 'Mexico' },
  { code: 'FM', name: 'Micronesia' },
  { code: 'MD', name: 'Moldova' },
  { code: 'MC', name: 'Monaco' },
  { code: 'MN', name: 'Mongolia' },
  { code: 'ME', name: 'Montenegro' },
  { code: 'MA', name: 'Morocco' },
  { code: 'MZ', name: 'Mozambique' },
  { code: 'MM', name: 'Myanmar' },
  { code: 'NA', name: 'Namibia' },
  { code: 'NR', name: 'Nauru' },
  { code: 'NP', name: 'Nepal' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'NI', name: 'Nicaragua' },
  { code: 'NE', name: 'Niger' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'KP', name: 'North Korea' },
  { code: 'MK', name: 'North Macedonia' },
  { code: 'NO', name: 'Norway' },
  { code: 'OM', name: 'Oman' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'PW', name: 'Palau' },
  { code: 'PS', name: 'Palestine' },
  { code: 'PA', name: 'Panama' },
  { code: 'PG', name: 'Papua New Guinea' },
  { code: 'PY', name: 'Paraguay' },
  { code: 'PE', name: 'Peru' },
  { code: 'PH', name: 'Philippines' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'QA', name: 'Qatar' },
  { code: 'RO', name: 'Romania' },
  { code: 'RU', name: 'Russia' },
  { code: 'RW', name: 'Rwanda' },
  { code: 'KN', name: 'Saint Kitts and Nevis' },
  { code: 'LC', name: 'Saint Lucia' },
  { code: 'VC', name: 'Saint Vincent and the Grenadines' },
  { code: 'WS', name: 'Samoa' },
  { code: 'SM', name: 'San Marino' },
  { code: 'ST', name: 'Sao Tome and Principe' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'SN', name: 'Senegal' },
  { code: 'RS', name: 'Serbia' },
  { code: 'SC', name: 'Seychelles' },
  { code: 'SL', name: 'Sierra Leone' },
  { code: 'SG', name: 'Singapore' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'SB', name: 'Solomon Islands' },
  { code: 'SO', name: 'Somalia' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'KR', name: 'South Korea' },
  { code: 'SS', name: 'South Sudan' },
  { code: 'ES', name: 'Spain' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'SD', name: 'Sudan' },
  { code: 'SR', name: 'Suriname' },
  { code: 'SE', name: 'Sweden' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'SY', name: 'Syria' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'TJ', name: 'Tajikistan' },
  { code: 'TZ', name: 'Tanzania' },
  { code: 'TH', name: 'Thailand' },
  { code: 'TL', name: 'Timor-Leste' },
  { code: 'TG', name: 'Togo' },
  { code: 'TO', name: 'Tonga' },
  { code: 'TT', name: 'Trinidad and Tobago' },
  { code: 'TN', name: 'Tunisia' },
  { code: 'TR', name: 'Turkey' },
  { code: 'TM', name: 'Turkmenistan' },
  { code: 'TV', name: 'Tuvalu' },
  { code: 'UG', name: 'Uganda' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'UY', name: 'Uruguay' },
  { code: 'UZ', name: 'Uzbekistan' },
  { code: 'VU', name: 'Vanuatu' },
  { code: 'VA', name: 'Vatican City' },
  { code: 'VE', name: 'Venezuela' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'YE', name: 'Yemen' },
  { code: 'ZM', name: 'Zambia' },
  { code: 'ZW', name: 'Zimbabwe' },
] as const;

/**
 * The set of valid `country` codes, derived from COUNTRIES so the two cannot
 * drift -- mirrors US_STATE_CODES as the server-side membership check.
 */
export const COUNTRY_CODES: readonly string[] = COUNTRIES.map((c) => c.code);

/** The default country when none is supplied. The campaign is US-based. */
export const DEFAULT_COUNTRY = 'US';

/* ------------------------------------------------------------------------- *
 * Admin analytics (aggregates only, zero PII)
 *
 * The shapes below back the token-gated /admin overview. Everything here is a
 * count, a rate, or a date bucket computed server-side from the stored records.
 * They carry NO personally identifiable information -- no name, email, note, or
 * photoKey ever appears in any of these, by construction. The submission
 * aggregates come from DynamoDB (Phase 1, GET /admin/stats). The traffic
 * aggregates come from real CloudFront access logs, summarised by a scheduled
 * Lambda into stats/traffic.json (Phase 2); when that file does not exist yet
 * the admin renders a neutral "not available" state rather than any number.
 * ------------------------------------------------------------------------- */

/** A single labelled bucket: a status, a USPS state code, or a ward number. */
export interface StatCount {
  key: string;
  count: number;
}

/** One UTC calendar day and how many things fell on it. */
export interface DailyCount {
  /** UTC calendar day, YYYY-MM-DD. */
  date: string;
  count: number;
}

/**
 * Response of `GET /admin/stats`. Aggregate submission analytics, PII-free.
 * Computed by src/functions/api/stats.ts from the raw SubmissionItem[] the
 * Lambda scans -- the raw items never leave the server.
 */
export interface StatsResponse {
  /** Total submissions across every status. */
  total: number;
  /** Count per moderation status. */
  byStatus: { pending: number; approved: number; rejected: number };
  /**
   * Approved / (approved + rejected). Null when nothing has been decided yet,
   * so the UI can say "not yet" instead of a misleading 0 percent.
   */
  approvalRate: number | null;
  /** Submissions per UTC day across the trailing window, oldest day first. */
  perDay: DailyCount[];
  /** Length of the perDay window, in days. */
  windowDays: number;
  /** Submissions per USPS state code, largest first. */
  byState: StatCount[];
  /** Submissions per DC ward '1'..'8', in ward order. */
  byWard: StatCount[];
  /**
   * Submissions per reference-link slug, largest first. Only submissions that
   * actually carried a ref are counted, so there is no "direct" bucket: the
   * total tile already covers overall volume, and inventing a bucket would put
   * a number on the chart that no reference link earned.
   */
  byRef: StatCount[];
  /** ISO 8601 time the aggregates were computed. */
  generatedAt: string;
}

/** One day of site traffic. Uniques is a rough proxy (distinct client IPs). */
export interface TrafficDay {
  /** UTC calendar day, YYYY-MM-DD. */
  date: string;
  pageViews: number;
  uniques: number;
}

/**
 * Shape of stats/traffic.json, written by the traffic-agg Lambda from real
 * CloudFront standard logs. The admin fetches it same-origin. GEO is out of
 * scope for v1: standard logs record the edge location, not the visitor's true
 * location, so no geography is reported here.
 */
export interface TrafficStats {
  /** ISO 8601 time the aggregation Lambda wrote this summary. */
  generatedAt: string;
  windowDays: number;
  perDay: TrafficDay[];
  totals: { pageViews: number; uniques: number };
  topPages: StatCount[];
  topReferrers: StatCount[];
  /**
   * Page views per reference-link slug (from `?ref=` in the logged cs-uri-query),
   * largest first, capped to the same top-N as the other rankings.
   *
   * OPTIONAL on purpose: the deployed stats/traffic.json predates this field and
   * only gains it after the next scheduled run, so the admin UI must treat
   * "absent" as "no data yet" and hide the chart rather than draw zeros.
   */
  byRef?: StatCount[];
  /** 4xx and 5xx counts and the request total they are a fraction of. */
  errors: { clientErrors: number; serverErrors: number; totalRequests: number };
  /**
   * Number of .gz log objects whose LastModified fell inside the read window
   * (the objects this summary was actually built from).
   *
   * Together with newestLogAt this is what tells "no data" apart from "zero
   * traffic": a summary of honest zeros built from zero log objects is NOT a
   * quiet month, it is a broken pipeline (see isLogStale in ./logFreshness).
   *
   * OPTIONAL for the same reason byRef is: absent means the summary predates the
   * field, so freshness is UNKNOWN -- the UI must not warn on it either way.
   */
  logObjectsInWindow?: number;
  /**
   * ISO 8601 LastModified of the newest .gz log object under the log
   * bucket/prefix, counting objects OLDER than the window too (during an outage
   * the newest log is weeks old, which is exactly what this must show).
   *
   * Absent: the summary predates the field (unknown). `null`: the Lambda looked
   * and there are no log objects at all under the prefix.
   */
  newestLogAt?: string | null;
}
